import { MultiPolygon, Point, Polygon } from "geojson";
import { PredefinedObstacle } from "./hooks/usePredefinedObstacle";
import { Shape } from "./screens/Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";

export type { Shape, Polygon };
export interface Aircraft {
  id: string;
  linked_to: string;
  make: string;
  model: string;
  wingspan: number;
  length: number;
  midline: number;
  tail_height: number;
  image: string;
  side_image: string;
  geom: any;
  nose: any;
  geomz: any;
  centroid: any;
  geomz_text: string;
  front_wheels: number;
  rear_wheels: number;
  wing_geom: any;
  wing_top: number;
  wing_bottom: number;
  tail_geom: any;
  tail_top: number;
  tail_bottom: number;
  vertical_tail_fin_geom: any;
  vertical_tail_fin_top: number;
  vertical_tail_fin_bottom: number;
  cabin_top: number;
  cabin_bottom: number;
  wheel_rotation_limit: number;
  reference_point_wing_bottom: number;
  reference_point_wing_top: number;
  reference_point_tail_bottom: number;
  reference_point_tail_top: number;
  reference_point_cabin_bottom: number;
  reference_point_cabin_top: number;
  reference_point_vertical_tail_fin_bottom: number;
  reference_point_vertical_tail_fin_top: number;
  propeller_geom: any;
  fuel_capacity: number;
  fuel_type: string;
  cruise_burn_gal_per_hour: number | null;
  cruise_speed_kts: number | null;
  reserves_gal: number | null;
  typical_cruise_altitude_ft: number | null;
  tail_wheel: boolean;
  locked: boolean;
  is_hidden: boolean;
  created_at: string;
  scaled_geom: Polygon;
  scaled_geom_full: Polygon;
  scaled_geomz_full: MultiPolygon;
  geom_scaled_buffer: Polygon;
  ramp_geom_scaled_buffer: Polygon;
  pivot_point: Point;
  simplify_pct: number;
  is_helicopter: boolean;
}

export type User = {
  id: string;
  email: string;
  _role: string;
  created_at: string;
};

export type FBOEmployee = {
  id: string;
  user_id: string;
  fbo_id: string;
  _role: string;
  user_display_name: string;
  user_name?: string;
  created_at: string;
};

export type FBO = {
  id: string;
  name: string;
  airport_code?: string;
  email_slug?: string;
  timezone?: string;
  subscription: string;
  user_role: string;
  sop_horizontal_spacing: number;
  sop_vertical_spacing: number;
  sop_overlap: boolean;
  sop_horizontal_spacing_tight: number;
  sop_vertical_spacing_tight: number;
  sop_overlap_tight: boolean;
  sop_horizontal_spacing_loose: number;
  sop_vertical_spacing_loose: number;
  sop_overlap_loose: boolean;
  sop_tug_type: string;
  sop_orientation_nose_in_threshold: number;
  sop_orientation_tail_in_threshold: number;
  sop_ramp_horizontal_spacing: number;
  nightly_base_rate: number;
  nightly_transient_rate: number;
  settings: FBOSettings;
  created_at: string;
};

export type FBOSettings = {
  displayEstimatedValueForFBOAdmin: boolean;
  displayEstimatedValueForManager: boolean;
  displayEstimatedValueForOperator: boolean;
  displayEstimatedValueForCSR: boolean;
  addTeamMembersForFBOAdmin: boolean;
  addTeamMembersForManager: boolean;
  addTeamMembersForOperator: boolean;
  addTeamMembersForCSR: boolean;
  modifyTenantsForFBOAdmin: boolean;
  modifyTenantsForManager: boolean;
  modifyTenantsForOperator: boolean;
  modifyTenantsForCSR: boolean;
  modifyLayoutForFBOAdmin: boolean;
  modifyLayoutForManager: boolean;
  modifyLayoutForOperator: boolean;
  modifyLayoutForCSR: boolean;
  createTripsForFBOAdmin: boolean;
  createTripsForManager: boolean;
  createTripsForOperator: boolean;
  createTripsForCSR: boolean;
  deleteTripsForFBOAdmin: boolean;
  deleteTripsForManager: boolean;
  deleteTripsForOperator: boolean;
  deleteTripsForCSR: boolean;
  modifyRatesForFBOAdmin: boolean;
  modifyRatesForManager: boolean;
  modifyRatesForOperator: boolean;
  modifyRatesForCSR: boolean;
  modifyInfrastructureForFBOAdmin: boolean;
  modifyInfrastructureForManager: boolean;
  modifyInfrastructureForOperator: boolean;
  modifyInfrastructureForCSR: boolean;
  modifyConfigurationForFBOAdmin: boolean;
  modifyConfigurationForManager: boolean;
  modifyConfigurationForOperator: boolean;
  modifyConfigurationForCSR: boolean;
  useFavoritesForFBOAdmin: boolean;
  useFavoritesForManager: boolean;
  useFavoritesForOperator: boolean;
  useFavoritesForCSR: boolean;
  viewOnlyForFBOAdmin: boolean;
  viewOnlyForManager: boolean;
  viewOnlyForOperator: boolean;
  viewOnlyForCSR: boolean;
  ignoreDeadSpaceForUtilization: boolean;
  forceDisplayNotes: boolean;
  useMetricSystem: boolean;
  enableScheduling: boolean;
  displayDetailedDecisionLog: boolean;
  useListHoldingArea?: boolean;
  enableAutoParking?: boolean;
  enableOpsCenter?: boolean;
  hangarDisplayOrder: string[];
  rampDisplayOrder: string[];
  defaultBuffer: number;
  defaultCAM: number;
  defaultCAMPerSqft: number;
};

export type Invitation = {
  id: string;
  claimed: boolean;
  email?: string;
  created_at: string;
};

// CSR Status type for customer-facing workflow
export type CsrStatus = "requested" | "confirmed" | "completed" | "cancelled";

export type Trip = {
  id: string;
  tenant_id: string;
  fbo_id: string;
  arrival_date: string;
  departure_date: string;
  status: "scheduled" | "active" | "completed" | "cancelled"; // Internal tracking status
  csr_status?: CsrStatus; // Customer-facing workflow status
  notes?: string;
  confirmation_code?: string;
  created_at: string;
  updated_at: string;
  version: number;
  external_source?: string;
  external_id?: string;
};

export interface ServiceItem {
  service_type: string;
  item_status: number;
}

export type ConciergeReservation = {
  id: string;
  trip_id: string;
  fbo_id: string;
  flightbridge_id: string;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  requested_services: (string | ServiceItem)[] | null;
  notes: string | null;
  flightbridge_location: string | null;
  requested_at: string | null;
  confirmed_at: string | null;
};

export type ServiceDisplayConfig = {
  id: string;
  fbo_id: string;
  service_code: string;
  display_mode: "primary" | "other";
  badge_label: string | null;
  badge_color: string | null;
  display_order: number;
};

// VIP Tag configuration per FBO
export type VipTagConfig = {
  id: string;
  fbo_id: string;
  tag_code: string;
  tag_label: string;
  tag_color: string;
  display_order: number;
  created_at?: string;
};

// VIP Tag for display (expanded from vip_tag_ids array)
export type VipTag = {
  id: string;
  tag_code: string;
  tag_label: string;
  tag_color: string;
};

export type TripWithTenant = Omit<Trip, "id"> & {
  trip_id: string; // The view returns trip_id instead of id
  tail_number?: string;
  owner_manager?: string;
  aircraft_type?: string;
  aircraft_model?: string;
  aircraft_manufacturer?: string;
  wingspan?: number;
  aircraft_length?: number;
  vip_tag_ids?: string[]; // Array of VIP tag IDs
  vip_tags?: VipTag[]; // Expanded VIP tag objects
};

export type Placement = {
  id: string;
  trip_id: string;
  hangar_id?: string;
  ramp_id?: string;
  start_time: string;
  end_time: string;
  x?: number;
  y?: number;
  angle?: number;
  created_at: string;
  updated_at: string;
  version: number;
  placement_source?: "manual" | "auto" | "import";
  is_locked?: boolean;
};

export type PlacementWithLocation = Placement & {
  hangar_id?: string;
  ramp_id?: string;
  hangar_name?: string;
  ramp_name?: string;
  location_name: string;
  location_type: "hangar" | "ramp" | "transient";
  // Additional fields from placements_at_time_with_aircraft function
  tenant_id?: string;
  tail_number?: string;
  aircraft_type?: string;
  entity_id?: string;
  owner_manager?: string;
  status?: "scheduled" | "active" | "completed" | "cancelled"; // Internal tracking status
  csr_status?: CsrStatus; // Customer-facing workflow status
  vip_tag_ids?: string[]; // Array of VIP tag IDs
  vip_tags?: VipTag[]; // Expanded VIP tag objects
  // Aircraft data
  aircraft_id?: string;
  aircraft_make?: string;
  aircraft_manufacturer?: string; // Alias for aircraft_make
  aircraft_model?: string;
  wingspan?: number;
  length?: number;
  aircraft_length?: number; // Alias for length
  // Trip dates
  arrival_date?: string;
  departure_date?: string;
  fbo_id?: string;
  // Geometry fields as GeoJSON
  scaled_geom_full?: any;
  scaled_geom?: any;
  pivot_point?: any;
  scaled_nose?: any;
  // Spot detection (calculated at data source)
  spot?: Shape;
};

export type PlacementUpdate = {
  action: "create" | "update" | "delete";
  id?: string;
  trip_id?: string;
  hangar_id?: string;
  ramp_id?: string;
  start_time?: string;
  end_time?: string;
  x?: number;
  y?: number;
  angle?: number;
  version?: number;
};

export type Position = {
  id: string;
  stack_id: string;
  entity_id: string;
  x: number;
  y: number;
  angle: number;
  preferences: Preference[];
  /**
   * When this position object was set / last updated
   */
  timestamp?: number;
};

export type AutocompletePosition = {
  id: string;
  hangar_id: string;
  x: number;
  y: number;
  angle: number;
  error?: number;
};

export enum Preference {
  PREFER_FRONT_ROW = "PREFER_FRONT_ROW",
  PREFER_BACK_ROW = "PREFER_BACK_ROW",
  PREFER_LEFT = "PREFER_LEFT",
  PREFER_RIGHT = "PREFER_RIGHT",
  PREFER_FRONT = "PREFER_FRONT",
  PREFER_BACK = "PREFER_BACK",
  // split hangar into thirds
  PREFER_LEFT_THIRD = "PREFER_LEFT_THIRD",
  PREFER_MIDDLE_THIRD = "PREFER_MIDDLE_THIRD",
  PREFER_RIGHT_THIRD = "PREFER_RIGHT_THIRD",
  NOSE_IN = "NOSE_IN",
  TAIL_IN = "TAIL_IN",
  // tail wheel is reversed b/c the image is upside down
  TAIL_WHEEL_NOSE_IN = "TAIL_WHEEL_TAIL_IN",
  TAIL_WHEEL_TAIL_IN = "TAIL_WHEEL_NOSE_IN",
  OK_TO_LEAVE_OUT = "OK_TO_LEAVE_OUT",
  LOCKED = "LOCKED",
}

export type Location<T> = T & {
  type: "hangar" | "ramp";
};

export type Entity<T> = T & {
  entity_type: string;
};

export enum TenantType {
  BASE = "base",
  TRANSIENT = "transient",
}

export type Stack = {
  id: string;
  name?: string;
  location_id?: string;
  isReference: boolean;
  tenants: Tenant[];
  movableObstacles: MovableObstacle[];
  options: PlacementOptions;
};

export type Tenant = {
  id: string;
  fbo_id: string;
  entity_id: string;
  placement_id: string;
  aircraft_id: string;
  type: string;
  tail_number: string;
  owner_manager: string;
  aircraft: Aircraft;
  created_at?: string;
  selected: boolean;
  position: Position;
  suggestedPositions?: AutocompletePosition[];
  last_hangared?: string;
  note?: string;
  note_last_updated_by?: string;
  note_last_updated?: string;
  trip_id?: string; // Optional - used in scheduled mode
  placement_source?: "manual" | "auto" | "import"; // Optional - used in scheduled mode
  is_locked?: boolean; // Optional - used in scheduled mode
  vip_tag_ids?: string[]; // Array of VIP tag IDs
  vip_tags?: VipTag[]; // Expanded VIP tag objects
  sandbox_hangar_id?: string | null; // If set, this tenant only exists in the specified sandbox
};

/**
 * Extended Tenant type for scheduled/temporal contexts
 * where temporal data (trip timing) is available
 */
export type ScheduledTenant = Tenant & {
  trip_id: string; // Required (not optional) in scheduled mode
  start_time: string; // ISO 8601 timestamp - trip arrival time
  end_time: string; // ISO 8601 timestamp - trip departure time
};

export type HoldingAreaEntry = {
  id: string;
  tenant_id: string;
  fbo_id: string;
  added_at: string;
  status: "active" | "assigned" | "removed";
  tenant?: Tenant; // Optional - populated when fetching entries with full tenant data
};

export type MovableObstacle = {
  id: string;
  fbo_id: string;
  entity_id: string;
  selected: boolean;
  placement_id: string;
  obstacle_id: string;
  obstacle: PredefinedObstacle;
  position: Position;
};

export interface ParamSet {
  label?: string;
  run_id: string;
  aircrafts_to_place: AircraftToPlace[];
  locked_aircrafts: LockedAircraft[];
  obstacles: ObstacleSlim[];
  hangar: HangarSlim;
  options: PlacementOptions;
}

export type ILayout = {
  id: string;
  fbo_id: string;
  name: string;
  stack: Stack;
};

export type BenchmarkRate = {
  id: string;
  fbo_id: string;
  type: string;
  tenant_name: string;
  aircraft_id: string;
  aircraft: Aircraft;
  rate: number;
};

export interface ILocation {
  width: number;
  depth: number;
}

export type RefPoint = {
  id: string;
  point: Point;
  hangar_id: string;
};

export type HeatmapCell = {
  id: string;
  hangar_id: string;
  index_width: number;
  index_depth: number;
  ref_point_id: string;
  geom: Polygon;
  ref_point?: RefPoint;
};

export type HeatmapArea = {
  area: Polygon | MultiPolygon;
  reference_point: Point;
};

export type LayerGroup = {
  id: string;
  name: string;
};

export type SOPOverrides = {
  sop_horizontal_spacing?: number;
  sop_vertical_spacing?: number;
  sop_overlap?: boolean;
  sop_horizontal_spacing_tight?: number;
  sop_vertical_spacing_tight?: number;
  sop_overlap_tight?: boolean;
  sop_horizontal_spacing_loose?: number;
  sop_vertical_spacing_loose?: number;
  sop_overlap_loose?: boolean;
  sop_ramp_horizontal_spacing?: number;
  sop_orientation_nose_in_threshold?: number;
  sop_orientation_tail_in_threshold?: number;
};

export type Hangar = {
  id: string;
  location_id: string;
  fbo_id: string;
  name: string;
  width: number;
  depth: number;
  height: number; // Legacy: single height (for backward compatibility)
  left_door: number;
  right_door: number;
  wall_spacing_left: number;
  wall_spacing_back: number;
  wall_spacing_right: number;
  obstacles: Obstacle[];
  stack: Stack;
  stacks: Stack[];
  last_stacked: string;
  created_at: string;
  garageDoors: GarageDoor[];
  base_tenant_value?: number;
  sqftCost?: number;
  heatmap_cells?: HeatmapCell[];
  autostacking_enabled?: boolean;
  towing_equipment_id?: string;
  bays?: HangarBay[]; // NEW: Optional bay definitions for stepped-eave hangars
  sop_overrides?: SOPOverrides;
  owner_user_id?: string | null; // If set, this is a private sandbox hangar owned by this user
};

export type CalibrationPoint = {
  id: string;
  ramp_id: string;
  x: number; // relative coordinates (0.0 to 1.0) - percentage of ramp width
  y: number; // relative coordinates (0.0 to 1.0) - percentage of ramp depth
  latitude: number;
  longitude: number;
};

export type Ramp = {
  id: string;
  location_id: string;
  fbo_id: string;
  version?: string;
  name: string;
  width: number;
  depth: number;
  reference_image: string;
  outline: Polygon;
  markings: Marking[];
  shapes: Shape[];
  calibration_points: CalibrationPoint[];
  stack: Stack;
  stacks: Stack[];
  autoparking_enabled?: boolean;
  towing_equipment_id?: string;
  last_stacked?: string;
  sop_overrides?: SOPOverrides;
  layer_groups?: LayerGroup[];
};

export type Marking = {
  id: string;
  ramp_id: string;
  type: MarkingType;
  geom: Polygon;
  selected?: boolean;
};

export enum MarkingType {
  OUTLINE = "Outline",
  REFERENCE_DISTANCE = "Reference Distance",
  RED_SOLID = "Red Solid",
  GREY = "Grey",
  WHITE_SOLID = "White Solid",
  WHITE_DASHED = "White Dashed",
  YELLOW_SOLID = "Yellow Solid",
  YELLOW_DASHED = "Yellow Dashed",
  YELLOW_SOLID_CURVED = "Yellow Solid Curved",
}

export type HangarSlim = {
  id: string;
  width: number;
  depth: number;
  left_door: number;
  right_door: number;
  garage_doors: GarageDoor[];
  bays?: HangarBay[];
};

export interface ObstacleSlim {
  geom: Polygon;
  height: number;
}

export type AircraftToPlace = {
  id: string;
  placement_id: string;
  angles_to_try: number[];
  preferences: Preference[];
  sectors_to_try: number[];
};

export enum ObstacleType {
  OBSTRUCTION = "OBSTRUCTION",
  NO_TOW_AREA = "NO_TOW_AREA",
  UNOBSTRUCTIVE = "UNOBSTRUCTIVE",
  CAUTION = "CAUTION",
}

export type Obstacle = {
  id: string;
  fbo_id: string;
  hangar_id: string;
  x: number;
  y: number;
  width: number;
  depth: number;
  height: number;
  geom: Polygon;
  active?: boolean;
  type?: ObstacleType;
};

export type GarageDoor = {
  id: string;
  fbo_id: string;
  hangar_id: string;
  wall: string;
  x: number;
  y: number;
  width: number;
  active?: boolean;
};

/**
 * Hangar Bay - Section of a stepped-eave hangar with specific height constraints
 *
 * Stepped-eave hangars have multiple bays with different roof heights.
 * Each bay is treated as an independent volume for placement purposes.
 *
 * Example:
 * - Bay A: 80ft wide, 28ft eave, 26ft clear → Small aircraft
 * - Bay B: 100ft wide, 34ft eave, 32ft clear → Midsize jets
 * - Bay C: 80ft wide, 40ft eave, 38ft clear → Large jets
 */
export type HangarBay = {
  id: string;
  hangar_id: string;
  fbo_id: string;
  geom: Polygon; // Bay boundary (GeoJSON polygon)
  eave_height: number; // Height at roofline (feet)
  clear_height: number; // Usable clearance under trusses (feet)
  created_at: string;
  updated_at: string;
};

/**
 * Bay orientation mode for UI editor
 * - Lateral: Bays arranged side-by-side (left to right)
 * - Longitudinal: Bays arranged front-to-back (shallow to deep)
 */
export type BayOrientation = "lateral" | "longitudinal";

/**
 * Bay definition in editor (before conversion to geometry)
 * Used in HangarBayEditor for table rows
 */
export type BayDefinition = {
  width?: number; // For lateral bays (feet)
  depth?: number; // For longitudinal bays (feet)
  eave_height: number; // Feet
  clear_height: number; // Feet
};

export type LockedAircraft = {
  id: string;
  placement_id: string;
  x: number;
  y: number;
  angle: number;
};

export type PlacementOptions = {
  wall_spacing_left: number;
  wall_spacing_back: number;
  wall_spacing_right: number;
  horizontal_spacing: number;
  vertical_spacing: number;
  overlap: boolean;
  tug_spacing: number;
  density_threshold: number;
  error_type?: string;
  sample_size?: number;
  corner_strategy: CornerStrategy[];
  cloud_position_prioritization?: boolean;
  xy_weights: number[];
  towing_equipment_id: string;
  // heatmap: HeatmapArea[];
};

export enum CornerStrategy {
  ANGLE_SHALLOW = "S",
  ANGLE_45 = "45",
}

export const CornerStrategies = [
  [CornerStrategy.ANGLE_45, CornerStrategy.ANGLE_45],
  [CornerStrategy.ANGLE_45, CornerStrategy.ANGLE_SHALLOW],
  [CornerStrategy.ANGLE_SHALLOW, CornerStrategy.ANGLE_45],
  [CornerStrategy.ANGLE_SHALLOW, CornerStrategy.ANGLE_SHALLOW],
];

export type Bounds = [geometric.Point, geometric.Point];

export type EntityPolygon = {
  entity_id: string;
  polygon: Polygon;
  bounds: Bounds;
};

// ============================================================================
// FlightAware AeroAPI Types (Ops Center)
// ============================================================================
// These types mirror the FlightAware AeroAPI response structure directly.
// No translation layer - field names match API exactly.

export type FlightAwareAirportRef = {
  code: string | null; // Best available: ICAO/IATA/LID
  code_icao: string | null;
  code_iata: string | null;
  code_lid: string | null;
  timezone: string | null; // IANA timezone
  name: string | null; // Airport name
  city: string | null; // Closest city
  airport_info_url: string | null;
};

export type FlightAwarePosition = {
  latitude: number;
  longitude: number;
  altitude: number; // 100s of feet
  altitude_change: "C" | "D" | "-"; // Climbing/Descending/Level
  groundspeed: number; // Knots
  heading: number | null; // 0-360 degrees
  timestamp: string; // ISO 8601 datetime
  update_type: "P" | "O" | "Z" | "A" | "M" | "D" | "X" | "S" | null;
};

export type Operator = {
  id: string;
  icao: string;
  iata: string | null;
  name: string | null;
  shortname: string | null;
  callsign: string | null;
  country: string | null;
  location: string | null;
  phone: string | null;
  url: string | null;
  wiki_url: string | null;
  alternatives: unknown | null;
  raw_data: unknown | null;
  created_at: string;
  updated_at: string;
};

export type FlightAwareFlight = {
  // Identification
  ident: string; // Flight identifier (operator+number or registration for GA)
  ident_icao: string | null;
  ident_iata: string | null;
  fa_flight_id: string; // FlightAware unique ID

  // Operator
  operator: string | null; // ICAO or IATA code
  operator_icao: string | null;
  operator_iata: string | null;
  operator_data: Operator | null; // Full operator object from web.operator table
  flight_number: string | null;

  // Aircraft
  registration: string | null; // Tail number
  aircraft_type: string | null; // ICAO aircraft type
  atc_ident: string | null; // ATC callsign

  // Route
  origin: FlightAwareAirportRef | null;
  destination: FlightAwareAirportRef | null;

  // Runways
  actual_runway_off: string | null;
  actual_runway_on: string | null;

  // Gates & Terminals
  gate_origin: string | null;
  gate_destination: string | null;
  terminal_origin: string | null;
  terminal_destination: string | null;
  baggage_claim: string | null;

  // Status
  status: string; // Human-readable status
  blocked: boolean;
  diverted: boolean;
  cancelled: boolean;
  position_only: boolean;

  // Codeshares
  inbound_fa_flight_id: string | null;
  codeshares: string[] | null; // ICAO codes
  codeshares_iata: string[] | null; // IATA codes

  // Delays (in SECONDS!)
  departure_delay: number | null;
  arrival_delay: number | null;

  // Progress
  progress_percent: number | null; // 0-100

  // Flight Plan
  filed_ete: number | null; // Runway-to-runway ETE (seconds)
  filed_airspeed: number | null; // Knots
  filed_altitude: number | null; // 100s of feet
  route: string | null;
  route_distance: number | null; // Statute miles

  // Cabin
  seats_cabin_business: number | null;
  seats_cabin_coach: number | null;
  seats_cabin_first: number | null;

  // Type
  type: "General_Aviation" | "Airline";

  // OOOI Times (Out-Off-On-In)
  // Gate times: *_out, *_in
  // Runway times: *_off, *_on
  scheduled_out: string | null; // ISO 8601 - Gate departure
  estimated_out: string | null;
  actual_out: string | null;

  scheduled_off: string | null; // ISO 8601 - Runway departure
  estimated_off: string | null;
  actual_off: string | null;

  scheduled_on: string | null; // ISO 8601 - Runway arrival
  estimated_on: string | null;
  actual_on: string | null;

  scheduled_in: string | null; // ISO 8601 - Gate arrival
  estimated_in: string | null;
  actual_in: string | null;

  // Position (live tracking)
  last_position: FlightAwarePosition | null;

  // Custom AirPlx fields
  services?: {
    hangar: boolean;
    fuel: boolean;
    concierge: boolean;
  };
  airplx_aircraft?: Partial<Aircraft>;
  flags?: {
    is_vip: boolean;
    is_favorite: boolean;
    is_ignored: boolean;
    vip_scope?: "aircraft" | "operator" | "flight";
  };
  concierge_reservations?: ConciergeReservation[];
};

/**
 * Historical flight from flightaware_history_flights table
 * Simpler than FlightAwareFlight (no custom AirPlx fields)
 */
export type TailHistoryFlight = {
  fa_flight_id: string;
  ident: string | null;
  ident_icao: string | null;
  registration: string | null;
  aircraft_type: string | null;

  operator_icao: string | null;
  operator_iata: string | null;
  operator_id: string | null;

  origin_code: string | null;
  origin_code_icao: string | null;
  origin_name: string | null;
  origin_city: string | null;

  destination_code: string | null;
  destination_code_icao: string | null;
  destination_name: string | null;
  destination_city: string | null;

  // OOOI Times (Out-Off-On-In) - matching database schema
  scheduled_out: string | null;
  estimated_out: string | null;
  actual_out: string | null;

  scheduled_off: string | null;
  estimated_off: string | null;
  actual_off: string | null;

  scheduled_on: string | null;
  estimated_on: string | null;
  actual_on: string | null;

  scheduled_in: string | null;
  estimated_in: string | null;
  actual_in: string | null;

  status: string | null;
  cancelled: boolean;
  diverted: boolean;
  blocked: boolean;

  route: string | null;
  route_distance: number | null;

  // Operator join (from PostgREST relation)
  operator?: Operator | null;
};

export type FlightAwareResponse = {
  arrivals: FlightAwareFlight[];
  departures: FlightAwareFlight[];
  scheduled_arrivals: FlightAwareFlight[];
  scheduled_departures: FlightAwareFlight[];
  links: {
    next: string;
  } | null;
  num_pages: number;
};

// ============================================================================
// Ops Center - Internal Types
// ============================================================================
// These types extend the API data with FBO-specific operational fields

export type OpsFlightFlag = {
  id: string;
  fbo_id: string;
  fa_flight_id?: string;
  registration?: string;
  operator_icao?: string;
  is_vip: boolean;
  is_favorite: boolean;
  is_ignored: boolean;
  vip_scope?: "aircraft" | "operator" | "flight";
  notes?: string;
  flagged_by_user_id?: string;
  flagged_at: string;
  created_at: string;
  updated_at: string;
};

export type OpsNote = {
  id: string;
  flight_id: string;
  user_id: string;
  user_display_name: string;
  note: string;
  created_at: string;
  tags?: string[];
  is_pinned?: boolean;
  updated_at?: string;
  edited_by?: string;
};

export type FuelRequest = {
  gallons: number;
  fuel_type: "JetA" | "100LL" | "Unleaded" | null;
  requested_by: string | null;
  completed: boolean;
  completed_at: string | null;
};

export type SpotAssignment = {
  spot_id: string | null;
  spot_name: string | null;
  assigned_at: string | null;
  assigned_by: string | null;
};

export type OpsConciergeRequest = {
  hangar_requested: boolean;
  crew_car_requested: boolean;
  catering_requested: boolean;
  ground_transport_requested: boolean;
  notes: string | null;
};

/**
 * Estimated fuel needed for an arriving aircraft
 * Used in Ops Center for resource planning and fuel truck allocation
 */
export type FuelEstimate = {
  /** Estimated gallons needed (rounded) */
  estimated_gallons: number;

  /** Estimated range (min-max) for display */
  estimated_range_gal: {
    min: number;
    max: number;
  };

  /** Aircraft size category used for estimation */
  size_category: string;

  /** Confidence level based on data availability */
  confidence: "high" | "medium" | "low";

  /** Human-readable explanation of how estimate was calculated */
  explanation: string;

  /** Flight distance used in calculation (nautical miles) */
  distance_nm?: number;
};

/**
 * Extends FlightAwareFlight with FBO operational data
 * Used in Ops Center UI for CSR workflow
 */
export type OpsFlightData = FlightAwareFlight & {
  // Internal AirPlx IDs (when synced to database)
  airplx_flight_id?: string;
  airplx_trip_id?: string;

  // Operational fields
  spot_assignment: SpotAssignment;
  fuel_request: FuelRequest | null;
  concierge: OpsConciergeRequest;
  internal_notes: OpsNote[];

  // Metadata
  last_updated: string;
  synced_from: "flightaware" | "flightbridge" | "manual";
};
